import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, ArrowRight, Clock, Users, Shield } from 'lucide-react';

interface LocationsProps {
  setCurrentPage: (page: string) => void;
}

const Locations: React.FC<LocationsProps> = ({ setCurrentPage }) => {
  const locations = [
    {
      city: "Cleveland",
      state: "OH",
      country: "United States",
      type: "Global Headquarters",
      features: [
        "Global Security Operations Center",
        "Research & Development Center",
        "Executive Offices"
      ]
    },
    {
      city: "Columbus",
      state: "OH",
      country: "United States",
      features: [
        "Regional Operations Center",
        "Training Facility",
        "Client Support Hub"
      ]
    },
    {
      city: "Dallas",
      state: "TX",
      country: "United States",
      features: [
        "Security Operations Center",
        "Technology Innovation Lab",
        "Regional Headquarters"
      ]
    },
    {
      city: "Phoenix",
      state: "AZ",
      country: "United States",
      features: [
        "Incident Response Center",
        "Client Services Hub",
        "Training Center"
      ]
    },
    {
      city: "Dubai",
      state: "",
      country: "UAE",
      features: [
        "Middle East Headquarters",
        "Security Operations Center",
        "Innovation Hub"
      ]
    },
    {
      city: "Dammam",
      state: "",
      country: "KSA",
      features: [
        "Regional Security Center",
        "Technical Support Hub",
        "Training Facility"
      ]
    },
    {
      city: "Pune",
      state: "",
      country: "India",
      features: [
        "Asia-Pacific Operations Center",
        "Development Center",
        "Support Hub"
      ]
    }
  ];

  const benefits = [
    {
      icon: Globe,
      title: "Global Reach",
      description: "Strategic locations worldwide to serve clients across all time zones"
    },
    {
      icon: Clock,
      title: "24/7 Operations",
      description: "Round-the-clock security monitoring and support services"
    },
    {
      icon: Users,
      title: "Local Expertise",
      description: "Teams with deep understanding of regional security landscapes"
    },
    {
      icon: Shield,
      title: "Unified Security",
      description: "Consistent security standards across all global locations"
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <MapPin className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Global Locations</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Serving clients worldwide with strategically located security operations centers and offices
          </motion.p>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {locations.map((location, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <MapPin className="h-7 w-7 text-white" />
                </div>
                <div className="ml-4">
                  <h3 className="text-2xl font-semibold">
                    {location.city}
                    {location.state && `, ${location.state}`}
                  </h3>
                  <p className="text-muted">{location.country}</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
              </div>

              <div>
                <h4 className="font-semibold mb-3">Key Features:</h4>
                <ul className="space-y-2">
                  {location.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-muted">
                      <Shield className="h-4 w-4 text-primary mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Global Presence Benefits</h2>
            <p className="text-lg text-muted">
              Advantages of our worldwide network of security operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mb-6">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our nearest location to discuss how we can help protect your organization
            </p>
            <button 
              onClick={() => setCurrentPage('contact')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Contact Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Locations;